import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import * as FileSaver from 'file-saver';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {SelectItem} from 'primeng/api';
import { Country } from '../../shared/types/country/country';
import { Region } from '../../shared/types/organization/region';
import { ReportsRestService } from '../../shared/services/reports/reports-rest.service';
import { FileUtilsService } from '../../shared/services/common/file/file-utils.service';
import { GlobalMessagesService } from '../../shared/services/messages/global.messages.service';

@Component({
  selector: 'wsd-user-count-tab',
  templateUrl: './user-count-tab.component.html',
  styleUrls: ['./user-count-tab.component.css']
})
export class UserCountTabComponent implements OnInit {

  private _countries = new BehaviorSubject<Country[]>([]);
  private _regions = new BehaviorSubject<Region[]>([]);
  public countryItems: SelectItem[] = [];
  public regionItems: SelectItem[] = [];
  public dateRange: string;

  @Output() private triggerSpinner = new EventEmitter();

  @Input()
  set allCountries(value) {
    this._countries.next(value);
  }

  @Input()
  set allRegions(value) {
    this._regions.next(value);
  }

  get allCountries(): Country[] {
    return this._countries.getValue();
  }

  get allRegions(): Region[] {
    return this._regions.getValue();
  }

  @Input()
  public userTotalCount: number;
  public userFilteredCount: number;
  public userCountForm: UntypedFormGroup;
  public dateFrom;
  public dateTo;

  constructor(
    private fb: UntypedFormBuilder,
    private reportsRestService: ReportsRestService,
    private fileUtils: FileUtilsService,
    private globalMessageService: GlobalMessagesService,
    private translate: TranslateService) { }

  public ngOnInit() {
    this.userCountForm = this.fb.group({
      countries: [''],
      regions: [''],
      dateFrom: [''],
      dateTo: ['']
    });
    this.getUserTotalCount();
    this.bindEvents();
    this.initCountry();
    this.initRegion();
    this.getDateRange();
  }

  public resetValue(): void {
    this.userFilteredCount = this.userTotalCount;
    this.userCountForm.reset();
  }


  public filter(): void {
    this.triggerSpinner.emit(true);
    this.reportsRestService.getFilteredCount(this.buildFormData(this.userCountForm.getRawValue())).subscribe(
      (res: any) => {
        this.userFilteredCount = res;
        this.triggerSpinner.emit(false);
      },
      (error) => {
        this.triggerSpinner.emit(false);
      }
    );
  }

  public exportByCountry(): void {
    this.triggerSpinner.emit(true);
    this.reportsRestService.exportByCountry(this.buildFormData(this.userCountForm.getRawValue())).subscribe(
      (response) => {
        this.downloadFile(response);
        this.triggerSpinner.emit(false);
      },
      (error) => {
        this.handleDownloadError(error);
        this.triggerSpinner.emit(false);
      }
    );
  }

  public exportByRegion(): void {
    this.triggerSpinner.emit(true);
    this.reportsRestService.exportByRegion(this.buildFormData(this.userCountForm.getRawValue())).subscribe(
      (response) => {
        this.downloadFile(response);
        this.triggerSpinner.emit(false);
      },
      (error) => {
        this.triggerSpinner.emit(false);
      }
    );
  }

  private initCountry() {
    this._countries
      .subscribe(x => {
        this.fillCountryDropdown(this.allCountries);
      });
  }

  private initRegion() {
    this._regions
      .subscribe(x => {
        this.fillRegionDropdown(this.allRegions);
      });
  }

  private getDateRange() {
    const today = new Date();
    const year = today.getFullYear();
    this.dateRange = `1990:${year}`;
  }

  private fillCountryDropdown(countries: Country[]) {
    this.countryItems = [];
    if (Array.isArray(countries)) {
      for (const country of countries) {
        this.countryItems.push({
          label: country.name,
          value: country
        });
      }
    }
  }

  private fillRegionDropdown(regions: Region[]) {
    this.regionItems = [];
    if (Array.isArray(regions)) {
      for (const region of regions) {
        this.regionItems.push({
          label: region.name,
          value: region
        });
      }
    }
  }

  private filterCountriesByRegion(regions: Region[]) {
    if (Array.isArray(regions) && regions.length > 0) {
      let countriesFilteredByRegion = [];
      for (const region of regions) {
        for (const country of this.allCountries) {
          if (country.region.keycloakId === region.keycloakId) {
            const index = countriesFilteredByRegion.findIndex(c => country.keycloakId === c.keycloakId);
            if (index < 0) {
              countriesFilteredByRegion.push(country);
            }
          }
        }
      }
      countriesFilteredByRegion = countriesFilteredByRegion.sort((a, b) => a.name.localeCompare(b.name));
      this.fillCountryDropdown(countriesFilteredByRegion);
    } else {
      this.fillCountryDropdown(this.allCountries);
    }
  }

  private bindEvents() {
    this.userCountForm.controls['regions'].valueChanges.subscribe(
      (selectedValue) => {
        this.filterCountriesByRegion(selectedValue);
      }
    );

    this.userCountForm.controls['countries'].valueChanges.subscribe(
      (selectedValue) => {
        if (Array.isArray(selectedValue) && selectedValue.length > 0) {
          this.userCountForm.controls['regions'].disable();
        } else {
          this.userCountForm.controls['regions'].enable();
        }
      }
    );
  }

  private getUserTotalCount(): void {
    this.triggerSpinner.emit(true);
    this.reportsRestService.getTotalUserCount().subscribe(
      (count: number) => {
        this.triggerSpinner.emit(false);
        this.userTotalCount = count;
        this.userFilteredCount = count;
      },
      (error) => {
        this.triggerSpinner.emit(false);
      }
    );
  }

  private buildFormData(object): FormData {
    const form: FormData = new FormData();
    if (Array.isArray(object.countries) && object.countries.length > 0) {
      object.countries.forEach(element => {
        form.append('countryList', element.keycloakId);
      });
    }
    if (Array.isArray(object.regions) && object.regions.length > 0) {
      object.regions.forEach(element => {
        form.append('regionList', element.keycloakId);
      });
    }
    if (object.dateFrom) {
      form.append('fromDate', object.dateFrom.toISOString());
    }
    if (object.dateTo) {
      form.append('toDate', object.dateTo.toISOString());
    }
    return form;
  }

  private downloadFile(response) {
    FileSaver.saveAs(response.body, this.fileUtils.extractFileName(response));
  }

  private handleDownloadError(rsp) {
    const msg = this.translate.instant('ASSESSMENT_RESULTS.SAVE_AS_EXCEL_ERROR_MESSAGE');
    this.globalMessageService.showErrorMessage(msg);
  }

}
